import styles from "./popup.module.scss";
import Button from "../button/Button";
import { useDispatch } from "react-redux";
import { setLoading } from "../../app/appSlice";

/**
 * Reusable Popup component.
 * 
 * Standard usage patterns:
 * 
 * 1. Confirmation popup:
 *    <Popup title="Are you sure?" description="Click Yes or Cancel" onSuccess={fn} onCancel={fn} />
 * 
 * 2. Alert popup (no cancel):
 *    <Popup title="Done!" successText="OK" cancelText={null} onSuccess={fn} />
 * 
 * 3. Custom content popup (e.g. with textarea):
 *    <Popup title="Reject" onSuccess={fn} onCancel={fn}>
 *      <textarea ... />
 *    </Popup>
 * 
 * Props:
 * - title: ReactNode — header message
 * - description: ReactNode — text below title (ignored if children provided)
 * - children: ReactNode — custom body content (takes priority over description)
 * - successText: string — success button label (default "Yes"), null to hide
 * - cancelText: string — cancel button label (default "Cancel"), null to hide
 * - warningMessage: string — error/warning text shown above buttons
 * - onSuccess: async function — called on success button click (wrapped in loading)
 * - onCancel: function — called on cancel button click
 */
const Popup = ({
  title,
  onSuccess = null,
  onCancel = null,
  successText = "Yes",
  cancelText = "Cancel",
  description = null,
  warningMessage = null,
  children = null,
  successDisabled = false
}) => {
  const dispatch = useDispatch();

  const handleSuccess = async () => {
    dispatch(setLoading({ isLoading: true }));
    if (onSuccess) {
      await onSuccess();
    }
    dispatch(setLoading({ isLoading: false }));
  };

  return (
    <div className={styles.layout}>
      <div className={styles.modal}>
        {title && <div className={styles.message}>{title}</div>}
        {children
          ? <div className={styles.confirmText}>{children}</div>
          : description && <div className={styles.confirmText}>{description}</div>
        }
        {warningMessage && <div><p className={styles.errorMessage}>{warningMessage}</p></div>}
        <div className={styles.footer}>
          {cancelText && (
            <Button style={styles.cancelButton} onClick={onCancel}>
              {cancelText}
            </Button>
          )}
          {successText && (
            <Button style={styles.successButton} onClick={handleSuccess} disabled={successDisabled}>
              {successText}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Popup;
